import { LinearProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useContext, useEffect } from "react";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthContext } from "~/auth/AuthProvider";
import ReloadPrompt from "~/components/ReloadPrompt";
import TrialLimitDialog from "~/components/TrialLimitDialog";
import Config from "~/config";
import redactSentryBreadcrumb, { redactReplayRecordingEvent, redactSentryTransaction } from "~/helpers/sentryRedaction";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useActionMenu from "~/hooks/useActionMenu";
import useContinuationIdentityFence from "~/hooks/useContinuationIdentityFence";
import useDisplayModePreference from "~/hooks/useDisplayModePreference";
import useEffectiveIsWideScreen from "~/hooks/useEffectiveIsWideScreen";
import usePageButtons from "~/hooks/usePageButtons";
import usePageTitle from "~/hooks/usePageTitle";
import useRecentDestinations from "~/hooks/useRecentDestinations";
import useSyncLanguagePreference from "~/hooks/useSyncLanguagePreference";
import KeyboardShortcutsProvider from "~/keyboard/KeyboardShortcutsProvider";
import Layout from "~/layout/Layout";
import WhatsNewProvider from "~/pages/home/WhatsNewProvider";
import TenantInactive from "~/pages/TenantInactive";
import AppThemeProvider from "~/providers/AppThemeProvider";
import NetworkStatusProvider from "~/providers/NetworkStatusProvider";
import TranslatedConfirmProvider from "~/providers/TranslatedConfirmProvider";
import UsedByPanelProvider from "~/providers/UsedByPanelProvider";
import { baseApiWithGraphql } from "~/services/baseApi";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { sentryBeforeSend } from "~/services/sentryRtkInstrumentation";
import { updateIsWideScreen } from "~/store/layoutSlice";
import { updateSettings } from "~/store/settingsSlice";
import useShrinkLegacyQueueRescue from "~mobile/availability/shrink/hooks/useShrinkLegacyQueueRescue";

const App = () => {
	const authContext = useContext(AuthContext);
	usePageTitle();
	useRecentDestinations();
	useActionMenu();
	usePageButtons();
	useSyncLanguagePreference();
	useContinuationIdentityFence();
	useShrinkLegacyQueueRescue();
	const dispatch = useAppDispatch();
	const tenantAccess = useAppSelector((state) => state.tenantAccess);
	const [displayModePreference] = useDisplayModePreference();
	const isWideScreen = useEffectiveIsWideScreen(displayModePreference, Config.navDrawerCoverWidth);

	useEffect(() => {
		dispatch(updateIsWideScreen(isWideScreen));
	}, [isWideScreen]);

	useEffect(() => {
		dispatch(
			updateSettings({ email: authContext.currentUser?.email || "UNKNOWN", tenantId: authContext.wsapiTenant || "" })
		);
	}, [authContext.currentUser, authContext.wsapiTenant]);

	// Flush RTK cache: queries deferred while tenantId was empty will re-run on next mount.
	useEffect(() => {
		if (authContext.loggedIn) {
			dispatch(baseApiWithGraphql.util.resetApiState());
			dispatch(apiEndpoints.util.prefetch("GetTenant", undefined, { force: true }));
		}
	}, [authContext.loggedIn]);

	if (!Sentry.getCurrentScope().getClient()) {
		Sentry.init({
			beforeBreadcrumb: redactSentryBreadcrumb,
			beforeSend: sentryBeforeSend,
			beforeSendTransaction: redactSentryTransaction,
			dsn: Config.sentryDSN,
			environment: Config.environment,
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.replayIntegration({
					beforeAddRecordingEvent: redactReplayRecordingEvent,
					blockAllMedia: true,
					maskAllText: true
				})
			],
			maxValueLength: 1000,
			release: Config.buildVersion,
			replaysOnErrorSampleRate: 0.1,
			replaysSessionSampleRate: Config.environment === "production" ? 0.01 : 0.1,
			sendDefaultPii: false,
			tracesSampleRate: Config.environment === "production" ? 0.1 : 1.0,
			transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport)
		});
	}

	Sentry.setUser({
		email: authContext.currentUser?.email || "notAvailable",
		username: authContext.currentUser?.displayName || "unknown"
	});

	if (!authContext.sessionChecked) {
		return <LinearProgress />;
	}

	return (
		<AppThemeProvider>
			<>
				<ToastContainer
					autoClose={5000}
					closeOnClick
					draggable
					hideProgressBar={false}
					newestOnTop={false}
					pauseOnFocusLoss
					pauseOnHover
					position="bottom-right"
					rtl={false}
					stacked
					theme="light"
					transition={Slide}
				/>
				<NetworkStatusProvider>
					{tenantAccess.inactive ? (
						<TenantInactive expiredTrial={tenantAccess.expiredTrial} onSignOut={() => authContext.redirectToLogout()} />
					) : (
						<>
							<TrialLimitDialog />
							<TranslatedConfirmProvider>
								<WhatsNewProvider>
									<ReloadPrompt />
									<KeyboardShortcutsProvider>
										<UsedByPanelProvider>
											<Layout />
										</UsedByPanelProvider>
									</KeyboardShortcutsProvider>
								</WhatsNewProvider>
							</TranslatedConfirmProvider>
						</>
					)}
				</NetworkStatusProvider>
			</>
		</AppThemeProvider>
	);
};
export default App;
